import React, { useState } from "react";
import { Link } from "react-router-dom";
import API from "../../utils/API";
import { Badge, Button, TextArea, useToast } from "../../ui";
import { formatDate } from "../../data/format";
import ReceiptRow from "./ReceiptRow";

/**
 * A staff question card (§B5 / §10.7c) — fix-its and the queue's "Ask the Fawn"
 * questions surface here under the Questions chip. Body + subject + a status
 * badge; when answered, the evidence-keyed answer as a quoted block plus the
 * ways to act: apply via the record's drawer, resolve with a plain-text finding
 * (§A6), or dismiss. awaiting_interactive reads "Waiting on a longer look" —
 * staff never see courier mechanics.
 */

const STATUS = {
  answered: { label: "Answered", variant: "info" },
  awaiting_interactive: { label: "Waiting on a longer look", variant: "neutral" },
  open: { label: "Waiting on the Fawn", variant: "neutral" },
  waiting_on_fawn: { label: "Waiting on the Fawn", variant: "neutral" }
};

function latestAnswer(question) {
  const answers = Array.isArray(question.answers) ? question.answers : [];
  return answers.length > 0 ? answers[answers.length - 1] : null;
}

function subjectHref(question) {
  return question.subjectModel === "Client" && question.subjectRef
    ? `/app/clients/${question.subjectRef}`
    : null;
}

function QuestionCard({ question, onDone }) {
  const toast = useToast();
  const [receipt, setReceipt] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [findings, setFindings] = useState(null); // null = closed; string = the textarea

  const status = STATUS[question.status] || STATUS.open;
  const answer = latestAnswer(question);
  const href = subjectHref(question);
  const askedWhen = formatDate(question.created_at); // "—" when absent/unparseable (E L-2)

  const resolve = async (resolution, note) => {
    setResolving(true);
    try {
      await API.resolveQuestion(question._id, note ? { resolution, note } : { resolution });
    } catch (err) {
      // 409 = already resolved (mirror mark-contacted) — treat as the outcome.
      if (!(err && err.response && err.response.status === 409)) {
        toast.error("Couldn't update that — try again.");
        setResolving(false);
        return;
      }
    }
    setReceipt({
      label: `${resolution === "dismissed" ? "Dismissed" : "Resolved"} — ${question.body}`,
      dismissed: resolution === "dismissed"
    });
    setResolving(false);
    if (onDone) {
      onDone(question);
    }
  };

  if (receipt) {
    return <ReceiptRow label={receipt.label} dismissed={receipt.dismissed} />;
  }

  return (
    <div className="fk-card feat-finding">
      <div className="fk-card__body">
        <div className="feat-finding__top">
          <Badge variant="info">Question</Badge>
          <Badge variant={status.variant}>{status.label}</Badge>
          {askedWhen !== "—" || question.askedByName ? (
            <span className="feat-finding__found">
              {askedWhen !== "—" ? `asked ${askedWhen}` : "asked"}
              {question.askedByName ? ` · ${question.askedByName}` : ""}
            </span>
          ) : null}
        </div>

        <div className="feat-finding__sentence">{question.body}</div>

        {answer ? (
          <div className="feat-quoteblock">
            {answer.text || answer.body}
            <span className="feat-quoteblock__who">
              the Fawn
              {answer.evidence ? ` · evidence: ${answer.evidence}` : ""}
              {answer.created_at ? ` · ${formatDate(answer.created_at)}` : ""}
            </span>
          </div>
        ) : null}

        {question.status === "answered" ? (
          <div className="feat-finding__actions">
            {href ? (
              <Link to={href}>
                <Button variant="primary">Apply via the drawer →</Button>
              </Link>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => setFindings((f) => (f === null ? "" : null))}
            >
              Resolve with findings
            </Button>
            <Button variant="ghost" loading={resolving} onClick={() => resolve("dismissed")}>
              Dismiss
            </Button>
          </div>
        ) : null}

        {findings !== null ? (
          <form
            className="feat-resolve"
            onSubmit={(e) => {
              e.preventDefault();
              resolve("resolved", findings.trim());
            }}
          >
            <TextArea
              rows={2}
              value={findings}
              onChange={(e) => setFindings(e.target.value)}
              placeholder="What did you find?"
              aria-label="Resolve with findings"
            />
            <Button type="submit" variant="primary" loading={resolving}>
              Mark resolved
            </Button>
          </form>
        ) : null}
      </div>
    </div>
  );
}

export default QuestionCard;
